import { z } from 'zod';
import { attachmentIdsSchema } from '../constants/attachments.js';
import { MAX_SMS_MEDIA_COUNT, SUPPORT_AI_CALL_STATUSES, SUPPORT_AUTO_REPLY_KINDS, SUPPORT_EMAIL_DELIVERY_STATUSES, SUPPORT_CALL_TARGET_KINDS, MANUAL_TICKET_CHANNELS, SUPPORT_CHANNELS, SUPPORT_CHAT_SESSION_STATUSES, SUPPORT_EMAIL_INTAKE_MODES, SUPPORT_IVR_ACTIONS, SUPPORT_IVR_DIGITS, SUPPORT_IVR_MODES, SUPPORT_MESSAGE_TYPES, SUPPORT_PHONE_VOICE_IDS, SUPPORT_SMS_WORDING_MODES, SUPPORT_TICKET_STATUSES, SUPPORT_TRANSCRIPT_STATUSES, SUPPORT_WEB_CHAT_AVAILABILITY_MODES, } from '../constants/support-states.js';
import { emailSchema, usPhoneSchema, uuidSchema } from './common.js';
// ---------------------------------------------------------------- tickets
const queryBoolean = z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
    .optional();
export const supportTicketListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(500).default(50),
    q: z.string().trim().max(200).optional(),
    status: z.enum(SUPPORT_TICKET_STATUSES).optional(),
    channel: z.enum(SUPPORT_CHANNELS).optional(),
    assignedStaffId: uuidSchema.optional(),
    customerId: uuidSchema.optional(),
    /** true = only tickets staff marked urgent (t1376). */
    urgent: queryBoolean,
    /** true = only tickets nobody is assigned to (t1376). */
    unassigned: queryBoolean,
    /** Opened at or after / before these instants (t1376). */
    createdFrom: z.iso.datetime().optional(),
    createdTo: z.iso.datetime().optional(),
    sort: z.enum(['lastMessageAt', 'assignedStaffName']).default('lastMessageAt'),
    order: z.enum(['asc', 'desc']).default('desc'),
});
export const supportTicketListItemSchema = z.object({
    id: uuidSchema,
    ticketNumber: z.string(),
    subject: z.string(),
    channel: z.enum(SUPPORT_CHANNELS),
    status: z.enum(SUPPORT_TICKET_STATUSES),
    contactPhone: z.string().nullable(),
    contactEmail: z.string().nullable(),
    customerId: uuidSchema.nullable(),
    customerName: z.string().nullable(),
    assignedStaffId: uuidSchema.nullable(),
    assignedStaffName: z.string().nullable(),
    urgent: z.boolean(),
    lastMessageAt: z.iso.datetime(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/** `chatActive` — a web chat is still live with an agent on this ticket, so
 *  staff can end it (t1119) without closing the ticket. */
export const supportTicketDetailSchema = supportTicketListItemSchema.extend({
    chatActive: z.boolean(),
});
/** What the AI receptionist (t0889) collected on one call, riding alongside
 *  its `ai_receptionist` message so staff can review the whole exchange
 *  without leaving the ticket thread. `null` on every other message type. */
export const supportAiCallSchema = z.object({
    status: z.enum(SUPPORT_AI_CALL_STATUSES),
    collected: z
        .object({
        name: z.string().nullable(),
        callbackNumber: z.string().nullable(),
        reason: z.string().nullable(),
    })
        .nullable(),
    transcript: z.array(z.object({ role: z.enum(['caller', 'ai']), text: z.string() })),
});
/** A picture on a text message (t1231) — metadata only; the bytes are fetched
 *  from `/support-tickets/:id/attachments/:attachmentId`. */
export const supportTicketAttachmentSchema = z.object({
    id: uuidSchema,
    filename: z.string(),
    contentType: z.string(),
    sizeBytes: z.number().int(),
});
export const supportTicketMessageSchema = z.object({
    id: uuidSchema,
    ticketId: uuidSchema,
    type: z.enum(SUPPORT_MESSAGE_TYPES),
    body: z.string(),
    attachments: z.array(supportTicketAttachmentSchema).default([]),
    fromNumber: z.string().nullable(),
    toNumber: z.string().nullable(),
    staffId: uuidSchema.nullable(),
    staffName: z.string().nullable(),
    hasRecording: z.boolean(),
    recordingDurationSeconds: z.number().int().nullable(),
    transcriptText: z.string().nullable(),
    transcriptStatus: z.enum(SUPPORT_TRANSCRIPT_STATUSES).nullable(),
    aiCall: supportAiCallSchema.nullable().default(null),
    /** Set when the system sent this message on its own (t1236). */
    autoReply: z.enum(SUPPORT_AUTO_REPLY_KINDS).nullable().default(null),
    /** Delivery state of an emailed reply; null for every other message. */
    emailStatus: z.enum(SUPPORT_EMAIL_DELIVERY_STATUSES).nullable().default(null),
    emailError: z.string().nullable().default(null),
    createdAt: z.iso.datetime(),
});
/** Earlier tickets from the same phone number (t1230). A text after Close opens a
 *  fresh ticket, so this carries the previous conversation onto the new one.
 *  `previous` is the newest earlier ticket with its messages; `others` are older
 *  ones, shown as links only. */
export const supportTicketEarlierSchema = z.object({
    previous: z
        .object({
        ticket: supportTicketListItemSchema,
        messages: z.array(supportTicketMessageSchema),
    })
        .nullable(),
    others: z.array(supportTicketListItemSchema),
});
export const supportTicketEventSchema = z.object({
    id: uuidSchema,
    fromStatus: z.enum(SUPPORT_TICKET_STATUSES).nullable(),
    toStatus: z.enum(SUPPORT_TICKET_STATUSES),
    actorType: z.enum(['staff', 'customer', 'kiosk', 'system']),
    actorLabel: z.string().nullable(),
    note: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const transitionSupportTicketSchema = z.object({
    to: z.enum(SUPPORT_TICKET_STATUSES),
    note: z.string().trim().max(500).nullable().default(null),
});
export const createSupportTicketSchema = z.object({
    subject: z.string().trim().min(1).max(200),
    /** What the contact really was (t1372); a plain Manual ticket when left out. */
    channel: z.enum(MANUAL_TICKET_CHANNELS).default('manual'),
    contactPhone: usPhoneSchema.nullable().optional(),
    contactEmail: emailSchema.nullable().optional(),
    customerId: uuidSchema.nullable().optional(),
    note: z.string().trim().max(2000).nullable().optional(),
});
/** Every field optional, at least one required — same shape as `updateRepairSchema`. */
export const updateSupportTicketSchema = z
    .object({
    subject: z.string().trim().min(1).max(200),
    assignedStaffId: uuidSchema.nullable(),
    customerId: uuidSchema.nullable(),
    contactPhone: usPhoneSchema.nullable(),
    contactEmail: emailSchema.nullable(),
    urgent: z.boolean(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export const replySupportTicketSchema = z
    .object({
    // Texts are capped at 1600 in the service; an emailed reply may run longer.
    body: z.string().trim().max(10000),
    /** Staged pictures to send as an MMS (t1231). Text is optional when there is one. */
    attachmentIds: z.array(uuidSchema).max(MAX_SMS_MEDIA_COUNT).default([]),
})
    .refine((v) => v.body.length > 0 || v.attachmentIds.length > 0, {
    message: 'Write a message or attach a picture',
    path: ['body'],
});
/** A text or email staff start from a customer's page, with no ticket yet
 *  (t1248). The conversation is opened for them by the server. */
export const startCustomerMessageSchema = z.discriminatedUnion('channel', [
    z.object({
        channel: z.literal('sms'),
        body: z.string().trim().min(1).max(1600),
    }),
    z.object({
        channel: z.literal('email'),
        subject: z.string().trim().min(1).max(200),
        body: z.string().trim().min(1).max(10000),
    }),
]);
export const addSupportNoteSchema = z
    .object({
    body: z.string().trim().max(2000).default(''),
    /** Files staged on the ticket for this note (t1249); a note may be only files. */
    attachmentIds: attachmentIdsSchema.default([]),
})
    .refine((v) => v.body.length > 0 || v.attachmentIds.length > 0, {
    message: 'Write a note or attach a file',
    path: ['body'],
});
// --------------------------------------------------------- routing settings
const businessHoursDaySchema = z
    .object({
    open: z.string().regex(/^\d{2}:\d{2}$/, 'Use HH:MM, 24-hour'),
    close: z.string().regex(/^\d{2}:\d{2}$/, 'Use HH:MM, 24-hour'),
})
    .nullable();
/** One entry per weekday; `null` means closed that day. The whole object
 *  being `null` means always open — no hours restriction at all. */
export const businessHoursSchema = z
    .object({
    mon: businessHoursDaySchema,
    tue: businessHoursDaySchema,
    wed: businessHoursDaySchema,
    thu: businessHoursDaySchema,
    fri: businessHoursDaySchema,
    sat: businessHoursDaySchema,
    sun: businessHoursDaySchema,
})
    .nullable();
export const supportSettingsSchema = z.object({
    timezone: z.string().min(1),
    businessHours: businessHoursSchema,
    voicemailGreeting: z.string(),
    afterHoursSmsAutoReply: z.string().nullable(),
    /** t1236: the closed-office reply to a new email ticket; null = off. */
    afterHoursEmailAutoReply: z.string().nullable(),
    /** t1236: answer inbound texts from the receptionist Q&A list. */
    smsAutoAnswerEnabled: z.boolean(),
    dialTimeoutSeconds: z.number().int().min(5).max(60),
    /** t1233: whether ticket texts carry the brand + opt-out on every text or
     *  only the first, then again after `smsWordingIntervalDays`. */
    smsWordingMode: z.enum(SUPPORT_SMS_WORDING_MODES),
    smsWordingIntervalDays: z.number().int().min(1).max(90),
    /** t0889: whether an AI receptionist picks up once the ring list is
     *  exhausted (or the call arrives after hours) instead of going straight
     *  to voicemail. Still requires an Anthropic key configured under
     *  Integrations — this toggle alone does not activate it. */
    aiReceptionistEnabled: z.boolean(),
    aiReceptionistGreeting: z.string(),
    /** t1405: the voice for everything the phone line says; 'default' = basic. */
    phoneVoice: z.enum(SUPPORT_PHONE_VOICE_IDS),
    /** t1100: the keypad phone menu ("press 1 for hours…") played before the
     *  ring list. Inert until at least one active key exists. `ivrGreeting` is
     *  only the opener — the "For X, press N" lines are built from the keys;
     *  `ivrClosingMessage` is spoken last (e.g. the text-us line), null = none. */
    ivrEnabled: z.boolean(),
    ivrMode: z.enum(SUPPORT_IVR_MODES),
    ivrGreeting: z.string(),
    ivrClosingMessage: z.string().nullable(),
    smsInboundWebhookUrl: z.string(),
    voiceInboundWebhookUrl: z.string(),
    /** t0948: whether the storefront chat widget appears at all, and whether
     *  it currently offers a human agent. `webChatAvailability` mirrors
     *  `SUPPORT_WEB_CHAT_AVAILABILITY_MODES` — `auto` reuses `businessHours`
     *  above rather than tracking a second schedule. */
    webChatEnabled: z.boolean(),
    webChatAvailability: z.enum(SUPPORT_WEB_CHAT_AVAILABILITY_MODES),
    webChatGreeting: z.string(),
    /** t1092: which mailbox feeds the Support tickets tab, and how. Inert
     *  until both are set — a mode with no mailbox does nothing. */
    emailIntakeMode: z.enum(SUPPORT_EMAIL_INTAKE_MODES),
    emailIntakeMailboxId: uuidSchema.nullable(),
    /** t1401: the recording played as the phone menu's opening greeting, if any.
     *  Set through its own upload route, so it is not in the update schema. */
    ivrGreetingAudio: z
        .object({
        filename: z.string(),
        sizeBytes: z.number().int(),
        uploadedAt: z.iso.datetime(),
    })
        .nullable(),
    updatedAt: z.iso.datetime().nullable(),
});
export const updateSupportSettingsSchema = z
    .object({
    timezone: z.string().min(1).max(100),
    businessHours: businessHoursSchema,
    voicemailGreeting: z.string().trim().min(1).max(500),
    afterHoursSmsAutoReply: z.string().trim().max(500).nullable(),
    afterHoursEmailAutoReply: z.string().trim().max(2000).nullable(),
    smsAutoAnswerEnabled: z.boolean(),
    dialTimeoutSeconds: z.number().int().min(5).max(60),
    smsWordingMode: z.enum(SUPPORT_SMS_WORDING_MODES),
    smsWordingIntervalDays: z.number().int().min(1).max(90),
    aiReceptionistEnabled: z.boolean(),
    aiReceptionistGreeting: z.string().trim().min(1).max(500),
    phoneVoice: z.enum(SUPPORT_PHONE_VOICE_IDS),
    ivrEnabled: z.boolean(),
    ivrMode: z.enum(SUPPORT_IVR_MODES),
    ivrGreeting: z.string().trim().min(1).max(500),
    ivrClosingMessage: z.string().trim().min(1).max(500).nullable(),
    webChatEnabled: z.boolean(),
    webChatAvailability: z.enum(SUPPORT_WEB_CHAT_AVAILABILITY_MODES),
    webChatGreeting: z.string().trim().min(1).max(500),
    emailIntakeMode: z.enum(SUPPORT_EMAIL_INTAKE_MODES),
    emailIntakeMailboxId: uuidSchema.nullable(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
// ------------------------------------------------------------- email intake
/** Staff turning one inbound mail message into a ticket (t1092). */
export const createTicketFromEmailSchema = z.object({ mailMessageId: uuidSchema });
export const createTicketFromEmailResponseSchema = z.object({
    ticketId: uuidSchema,
    /** False when the message already had a ticket (or joined an open one). */
    created: z.boolean(),
});
// ------------------------------------------------------------- web chat
/** One turn of the pre-handoff AI phase, as shown to the shopper and frozen
 *  into a `chat_ai` message body at handoff. */
export const supportChatTurnSchema = z.object({
    role: z.enum(['shopper', 'ai']),
    text: z.string(),
});
export const supportChatAvailabilitySchema = z.object({
    enabled: z.boolean(),
    agentsAvailable: z.boolean(),
    aiAvailable: z.boolean(),
    greeting: z.string(),
});
/** A guest's reply details, collected right before a ticket is created (a
 *  signed-in shopper's account supplies this instead — see the service). At
 *  least one of email/phone is required so staff have somewhere to reply. */
export const supportChatContactSchema = z
    .object({
    name: z.string().trim().min(1).max(100),
    email: emailSchema.nullable().optional(),
    phone: usPhoneSchema.nullable().optional(),
})
    .refine((c) => !!c.email || !!c.phone, {
    message: 'An email or phone number is needed so we can follow up',
    path: ['email'],
});
/** The storefront "Contact us" page (t1075). Same reply-channel rule as the
 *  chat widget's guest form, plus the SMS opt-in: ticking it without a number
 *  would record a consent nobody can act on, so a phone is required then. Not
 *  built by extending `supportChatContactSchema` — that one carries a refine. */
export const contactFormSchema = z
    .object({
    name: z.string().trim().min(1).max(100),
    email: emailSchema.nullable().default(null),
    phone: usPhoneSchema.nullable().default(null),
    message: z.string().trim().min(1).max(5000),
    smsOptIn: z.boolean().default(false),
})
    .refine((c) => !!c.email || !!c.phone, {
    message: 'An email or phone number is needed so we can follow up',
    path: ['email'],
})
    .refine((c) => !c.smsOptIn || !!c.phone, {
    message: 'Enter a mobile number to receive texts',
    path: ['phone'],
});
export const contactFormResponseSchema = z.object({ ticketNumber: z.string() });
/** Every session-scoped chat endpoint takes its token in the POST body
 *  rather than a header or the URL — the storefront's shared API client has
 *  no per-request header option, and this keeps the token out of server
 *  access logs (same reasoning as the wiki share-link resolve route). */
const supportChatTokenSchema = z.object({
    token: z.string().min(20).max(200),
});
export const startSupportChatSessionSchema = z.object({});
export const postSupportChatMessageSchema = supportChatTokenSchema.extend({
    body: z.string().trim().min(1).max(2000),
});
/** What the shopper says the chat is about (t1121) — becomes the ticket
 *  subject. Optional: blank falls back to "Web chat — <name>". Top-level
 *  rather than inside `contact`, which a signed-in shopper never sends. */
const supportChatSubjectSchema = z.string().trim().max(200).optional();
export const requestSupportChatAgentSchema = supportChatTokenSchema.extend({
    contact: supportChatContactSchema.optional(),
    subject: supportChatSubjectSchema,
});
export const supportChatOfflineMessageSchema = supportChatTokenSchema.extend({
    body: z.string().trim().min(1).max(2000),
    contact: supportChatContactSchema.optional(),
    subject: supportChatSubjectSchema,
});
export const supportChatStateRequestSchema = supportChatTokenSchema;
export const closeSupportChatSessionSchema = supportChatTokenSchema;
export const returnToSupportChatAssistantSchema = supportChatTokenSchema;
/** One message the shopper is allowed to see — deliberately narrower than
 *  `supportTicketMessageSchema`: notes, SMS and call rows on a ticket that
 *  also happens to have been reached via chat must never reach the widget. */
export const supportChatVisibleMessageSchema = z.object({
    id: uuidSchema,
    direction: z.enum(['inbound', 'outbound']),
    body: z.string(),
    /** Whether a staff member sent this reply. The staff member's identity is
     *  deliberately never sent to the shopper — the widget shows a fixed label. */
    fromStaff: z.boolean(),
    createdAt: z.iso.datetime(),
});
export const supportChatStateSchema = z.object({
    status: z.enum(SUPPORT_CHAT_SESSION_STATUSES),
    agentsAvailable: z.boolean(),
    aiAvailable: z.boolean(),
    /** The frozen (pre-handoff) AI phase — empty once a ticket exists, since
     *  it has already been written into the ticket as a `chat_ai` message. */
    transcript: z.array(supportChatTurnSchema),
    ticketStatus: z.enum(SUPPORT_TICKET_STATUSES).nullable(),
    /** Whether any staff reply exists yet — the difference between "waiting
     *  for an agent" and "connected" once `status === 'agent'`. */
    hasAgentReplied: z.boolean(),
    messages: z.array(supportChatVisibleMessageSchema),
});
export const startSupportChatResponseSchema = z.object({
    token: z.string(),
    state: supportChatStateSchema,
});
export const postSupportChatMessageResponseSchema = supportChatStateSchema.extend({
    /** The AI turn just added asked to be handed to a human — the widget
     *  should highlight "Chat with an agent" rather than the shopper having to
     *  notice it themselves. */
    suggestAgent: z.boolean(),
});
// ----------------------------------------------------------- call routing
export const supportCallTargetSchema = z.object({
    id: uuidSchema,
    kind: z.enum(SUPPORT_CALL_TARGET_KINDS),
    label: z.string(),
    phoneNumber: z.string().nullable(),
    incomingPhoneNumber: z.string().nullable(),
    staffId: uuidSchema.nullable(),
    staffName: z.string().nullable(),
    priority: z.number().int(),
    active: z.boolean(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/** `phone` needs a number; `client` (a staff member's browser, t0869) needs a
 *  staff member instead. The superRefine mirrors the DB check constraint. */
export const createSupportCallTargetSchema = z
    .object({
    kind: z.enum(SUPPORT_CALL_TARGET_KINDS).default('phone'),
    label: z.string().trim().min(1).max(100),
    phoneNumber: usPhoneSchema.nullable().optional(),
    incomingPhoneNumber: usPhoneSchema.nullable().optional(),
    staffId: uuidSchema.nullable().optional(),
    priority: z.number().int().min(0).max(1000).default(0),
    active: z.boolean().default(true),
})
    .superRefine((v, ctx) => {
    if (v.kind === 'phone' && !v.phoneNumber) {
        ctx.addIssue({ code: 'custom', path: ['phoneNumber'], message: 'A phone number is required' });
    }
    if (v.kind === 'client' && !v.staffId) {
        ctx.addIssue({ code: 'custom', path: ['staffId'], message: 'A staff member is required' });
    }
});
export const updateSupportCallTargetSchema = z
    .object({
    kind: z.enum(SUPPORT_CALL_TARGET_KINDS),
    label: z.string().trim().min(1).max(100),
    phoneNumber: usPhoneSchema.nullable(),
    incomingPhoneNumber: usPhoneSchema.nullable(),
    staffId: uuidSchema.nullable(),
    priority: z.number().int().min(0).max(1000),
    active: z.boolean(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
// -------------------------------------------------------- phone menu keys
export const supportIvrOptionSchema = z.object({
    id: uuidSchema,
    digit: z.enum(SUPPORT_IVR_DIGITS),
    label: z.string(),
    action: z.enum(SUPPORT_IVR_ACTIONS),
    message: z.string().nullable(),
    position: z.number().int(),
    active: z.boolean(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/** `label` completes "For {label}, press {digit}."; a `message` key also needs
 *  the text to speak. The superRefine mirrors the DB check constraint. */
export const createSupportIvrOptionSchema = z
    .object({
    digit: z.enum(SUPPORT_IVR_DIGITS),
    label: z.string().trim().min(1).max(100),
    action: z.enum(SUPPORT_IVR_ACTIONS).default('message'),
    message: z.string().trim().min(1).max(1000).nullable().optional(),
    position: z.number().int().min(0).max(1000).default(0),
    active: z.boolean().default(true),
})
    .superRefine((v, ctx) => {
    if (v.action === 'message' && !v.message) {
        ctx.addIssue({ code: 'custom', path: ['message'], message: 'A message is required' });
    }
});
export const updateSupportIvrOptionSchema = z
    .object({
    digit: z.enum(SUPPORT_IVR_DIGITS),
    label: z.string().trim().min(1).max(100),
    action: z.enum(SUPPORT_IVR_ACTIONS),
    message: z.string().trim().min(1).max(1000).nullable(),
    position: z.number().int().min(0).max(1000),
    active: z.boolean(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
// ------------------------------------------------------ browser softphone
/** Returned by `POST /support/voice/token` — a short-lived Twilio Voice
 *  Access Token for the signed-in staff member's browser client (t0869).
 *  `receiveIncoming` tells the frontend whether to register for inbound
 *  calls at all: only true when this staff member has an active `client`
 *  call target, so availability is controlled from Support settings. */
export const voiceTokenResponseSchema = z.object({
    token: z.string(),
    identity: z.string(),
    ttlSeconds: z.number().int(),
    receiveIncoming: z.boolean(),
});
/** One dialable linked phone for outgoing calls (t0887) — an active
 *  `kind: 'phone'` call target, as listed by `GET /support/voice/call-options`.
 *  Reuses the same ring-list rows Support Settings already manages, rather
 *  than introducing a second place to configure external numbers. */
export const voiceCallOptionSchema = z.object({
    id: uuidSchema,
    label: z.string(),
    phoneNumber: z.string(),
    staffId: uuidSchema.nullable(),
});
/** `POST /support/voice/callback-calls` (t0887): ring the chosen linked
 *  phone first; the customer is dialed only once that leg answers. */
export const startCallbackCallSchema = z.object({
    to: usPhoneSchema,
    callTargetId: uuidSchema,
});
export const callbackCallResponseSchema = z.object({
    callSid: z.string(),
});
/** A browser the signed-in staff member has open with the softphone running
 *  (t1229), reported by `POST /support/voice/devices/heartbeat`. `deviceKey`
 *  is a random id the browser keeps in localStorage; `label` is a friendly
 *  name such as "iPad · Safari". */
export const softphoneDeviceHeartbeatSchema = z.object({
    deviceKey: z.string().regex(/^[a-z0-9]{16,32}$/),
    label: z.string().trim().min(1).max(60),
    /** False when this browser has been told not to ring for customer calls
     *  (t1239). Older tabs omit it and keep ringing. */
    ringIncoming: z.boolean().default(true),
});
export const softphoneDeviceSchema = z.object({
    deviceKey: z.string(),
    label: z.string(),
    ringIncoming: z.boolean(),
    lastSeenAt: z.iso.datetime(),
});
/** `POST /support/voice/device-calls` (t1229): ring one of the caller's other
 *  open browsers; the customer is dialed only once that browser answers. */
export const startDeviceCallSchema = z.object({
    to: usPhoneSchema,
    deviceKey: softphoneDeviceHeartbeatSchema.shape.deviceKey,
});
const twilioCallSidSchema = z.string().regex(/^CA[0-9a-f]{32}$/);
/** One place a live call can be handed to (t1228) — an active entry of the
 *  Support settings call-target list, browser softphone or outside phone. */
export const voiceTransferTargetSchema = z.object({
    id: uuidSchema,
    label: z.string(),
    kind: z.enum(['client', 'phone']),
    staffId: uuidSchema.nullable(),
});
/** `POST /support/voice/hold` (t1228). Exactly one of `callSid` (this
 *  browser's live leg) or `customerCallSid` (a caller already resumed once —
 *  no longer a child of the browser leg, so the browser has to name them). */
export const holdCallSchema = z
    .object({
    callSid: twilioCallSidSchema.optional(),
    customerCallSid: twilioCallSidSchema.optional(),
})
    .refine((v) => Boolean(v.callSid) !== Boolean(v.customerCallSid), {
    message: 'Send either callSid or customerCallSid',
});
export const holdCallResponseSchema = z.object({ customerCallSid: twilioCallSidSchema });
/** `POST /support/voice/transfer` (t1228). Exactly one of `callSid` (this
 *  browser's live leg) or `customerCallSid` (a call already on hold). */
export const transferCallSchema = z
    .object({
    callSid: twilioCallSidSchema.optional(),
    customerCallSid: twilioCallSidSchema.optional(),
    targetId: uuidSchema,
    announce: z.boolean().default(false),
})
    .refine((v) => Boolean(v.callSid) !== Boolean(v.customerCallSid), {
    message: 'Send either callSid or customerCallSid',
});
/** `POST /support/voice/hold/end` (t1228): hang up a caller who is on hold. */
export const endHeldCallSchema = z.object({ customerCallSid: twilioCallSidSchema });
// -------------------------------------------------------- canned responses
export const supportCannedResponseSchema = z.object({
    id: uuidSchema,
    title: z.string(),
    body: z.string(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const createSupportCannedResponseSchema = z.object({
    title: z.string().trim().min(1).max(100),
    body: z.string().trim().min(1).max(2000),
});
export const updateSupportCannedResponseSchema = z
    .object({
    title: z.string().trim().min(1).max(100),
    body: z.string().trim().min(1).max(2000),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
// ------------------------------------------------------- close notes (t1347)
/** The notes offered when closing or resolving a ticket until staff edit the list. */
export const DEFAULT_TICKET_CLOSE_NOTES = [
    'Answered',
    'No answer – left voicemail',
    'No answer – no voicemail',
    'Customer will call back',
    'Came in',
    'Resolved by text',
    'Wrong number',
    'Spam',
];
export const ticketCloseNotesSchema = z.object({
    notes: z
        .array(z.string().trim().min(1).max(100))
        .max(30)
        .refine((n) => new Set(n.map((x) => x.toLowerCase())).size === n.length, {
        message: 'Each note can only appear once',
    }),
});
// ------------------------------------------------- AI receptionist FAQs
/** One question/answer pair the AI receptionist (t0889) can draw on when a
 *  caller asks something — same shape as canned responses, but consumed by
 *  the model's prompt rather than offered to staff in the reply composer. */
export const supportReceptionistFaqSchema = z.object({
    id: uuidSchema,
    question: z.string(),
    answer: z.string(),
    active: z.boolean(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const createSupportReceptionistFaqSchema = z.object({
    question: z.string().trim().min(1).max(300),
    answer: z.string().trim().min(1).max(2000),
    active: z.boolean().default(true),
});
export const updateSupportReceptionistFaqSchema = z
    .object({
    question: z.string().trim().min(1).max(300),
    answer: z.string().trim().min(1).max(2000),
    active: z.boolean(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
//# sourceMappingURL=support.js.map